import type { ReinforcementState } from "./logistics-types.js";
import { exactRecordKeys, recordObject } from "./record-validation.js";

export function validateWaveSeconds(value: unknown): number {
  if (!Number.isInteger(value) || (value as number) < 5 || (value as number) > 120)
    throw new TypeError("Gather time must be 5–120 whole game seconds");
  return value as number;
}

export function setWaveTimer(
  state: ReinforcementState,
  id: string,
  seconds: number,
  now: number,
): ReinforcementState {
  validateWaveSeconds(seconds);
  const nextAtMs = now + seconds * 1000;
  if (!Number.isSafeInteger(nextAtMs) || now < 0) throw new TypeError("Invalid wave clock");
  return { ...state, timers: { ...state.timers, [id]: { seconds, nextAtMs } } };
}

export function restartWaveTimer(
  state: ReinforcementState,
  id: string,
  now: number,
): ReinforcementState {
  const timer = state.timers?.[id];
  return timer ? setWaveTimer(state, id, timer.seconds, now) : state;
}

export function validateWaveTimers(
  value: unknown,
  borders: readonly string[],
): NonNullable<ReinforcementState["timers"]> {
  const source = recordObject(value, "wave timers");
  return Object.fromEntries(
    Object.entries(source).map(([id, value]) => {
      if (!borders.includes(id)) throw new TypeError("Invalid wave timer destination");
      const timer = recordObject(value, "wave timer");
      exactRecordKeys(timer, ["seconds", "nextAtMs"]);
      if (!Number.isSafeInteger(timer.nextAtMs) || (timer.nextAtMs as number) < 0)
        throw new TypeError("Invalid wave deadline");
      return [
        id,
        Object.freeze({
          seconds: validateWaveSeconds(timer.seconds),
          nextAtMs: timer.nextAtMs as number,
        }),
      ];
    }),
  );
}
